import React from 'react';

const YouTubeSubscriptionList = ({ 
  subscriptions, 
  loading, 
  onEdit, 
  onDelete, 
  onResubscribe,
  onPause,
  onResume
}) => {
  const getStatusBadge = (subscription) => {
    const status = subscription.subscription_status;
    const isExpiringSoon = subscription.lease_expires_at && 
      new Date(subscription.lease_expires_at) < new Date(Date.now() + 24 * 60 * 60 * 1000);

    let badgeClass = 'bg-secondary';
    let icon = 'circle';

    if (status === 'active') {
      badgeClass = isExpiringSoon ? 'bg-warning' : 'bg-success';
      icon = isExpiringSoon ? 'exclamation-triangle' : 'check-circle';
    } else if (status === 'pending') {
      badgeClass = 'bg-info';
      icon = 'hourglass-split';
    } else if (status === 'error') {
      badgeClass = 'bg-danger';
      icon = 'x-circle';
    } else if (status === 'unsubscribed') {
      badgeClass = 'bg-secondary';
      icon = 'dash-circle';
    }

    return (
      <span className={`badge ${badgeClass}`}>
        <i className={`bi bi-${icon} me-1`}></i>
        {status}
        {isExpiringSoon && status === 'active' && ' (expires soon)'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return 'Expired';
    if (diffDays > 0) return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    return 'soon';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading subscriptions...</p>
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <i className="bi bi-youtube display-1 text-danger"></i>
          <h5 className="mt-3">No subscriptions configured</h5>
          <p className="text-muted">
            Create your first YouTube channel subscription to start receiving notifications
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Channel Subscriptions ({subscriptions.length})</h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Type</th>
                <th>Teams Destination</th>
                <th>Status</th>
                <th>Notifications</th>
                <th>Last Verified</th>
                <th>Lease Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td>
                    <div>
                      <strong>{subscription.channel_name}</strong>
                      {subscription.channel_url && (
                        <div>
                          <a 
                            href={subscription.channel_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-muted small"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            View Channel
                          </a>
                        </div>
                      )}
                      <small className="text-muted d-block font-monospace">
                        {subscription.channel_id}
                      </small>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${subscription.subscription_type === 'Research' ? 'bg-info text-dark' : 'bg-primary'}`}>
                      {subscription.subscription_type || 'Teams'}
                    </span>
                  </td>
                  <td>
                    <div>
                      {subscription.teams_channel_name && (
                        <strong className="d-block">{subscription.teams_channel_name}</strong>
                      )}
                      {!subscription.teams_channel_name ? (
                        <small className="text-muted">
                          {subscription.subscription_type === 'Research'
                            ? 'Research workflow (no Teams destination)'
                            : 'No Teams channel set'}
                        </small>
                      ) : (
                        <small className="text-muted">
                          <i className="bi bi-microsoft-teams me-1"></i>
                          MS Teams Webhook
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(subscription)}
                    {!subscription.is_active && (
                      <div className="mt-1">
                        <span className="badge bg-secondary">
                          <i className="bi bi-pause-circle me-1"></i>
                          Paused
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div>
                      <strong>{subscription.notification_count || 0}</strong>
                      {subscription.last_notification_at && (
                        <small className="text-muted d-block">
                          Last: {formatDate(subscription.last_notification_at)}
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    <small>{formatDate(subscription.last_verified_at)}</small>
                  </td>
                  <td>
                    {subscription.lease_expires_at ? (
                      <div>
                        <small className="d-block">{formatDate(subscription.lease_expires_at)}</small>
                        <small className="text-muted">
                          ({formatRelativeTime(subscription.lease_expires_at)})
                        </small>
                      </div>
                    ) : (
                      <small className="text-muted">N/A</small>
                    )}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => onEdit(subscription)}
                        title="Edit subscription"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      {subscription.is_active ? (
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => onPause(subscription.id)}
                          title="Pause notifications"
                        >
                          <i className="bi bi-pause-circle"></i>
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-success"
                          onClick={() => onResume(subscription.id)}
                          title="Resume notifications"
                        >
                          <i className="bi bi-play-circle"></i>
                        </button>
                      )}
                      {(subscription.subscription_status === 'unsubscribed' || 
                        subscription.subscription_status === 'error' ||
                        (subscription.lease_expires_at && 
                         new Date(subscription.lease_expires_at) < new Date())) && (
                        <button
                          className="btn btn-outline-info"
                          onClick={() => onResubscribe(subscription.id)}
                          title="Resubscribe to channel"
                        >
                          <i className="bi bi-arrow-clockwise"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => onDelete(subscription.id)}
                        title="Delete subscription"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-footer text-muted">
        <small>
          <i className="bi bi-info-circle me-1"></i>
          Subscriptions are verified by YouTube and typically expire after 5 days. They will be automatically renewed.
        </small>
      </div>
    </div>
  );
};

export default YouTubeSubscriptionList;
